import { useCallback, useEffect, useMemo, useState } from 'react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { Switch } from '@headlessui/react';
import { CheckIcon, ClipboardDocumentIcon, PencilIcon, QrCodeIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useApiClient, buildRestaurantPath } from '../lib/api';
import { useConfig } from '../providers/config-provider';
import { useAuth } from '../providers/auth-provider';
import { useNotification } from '../providers/notification-provider';
import QrPreview from '../components/common/QrPreview';
import StatusPill from '../components/common/StatusPill';
import Pagination from '../components/common/Pagination';
import SortableTh from '../components/common/SortableTh';
import ContextHelpButton from '../components/help/ContextHelpButton';
import ContextHelpDrawer from '../components/help/ContextHelpDrawer';
import { TABLES_HELP_SECTIONS } from '../components/help/waiterCallHelpContent';

const schema = z.object({
  numberLabel: z.string().min(1, 'Informe o nome ou número'),
  area: z.string().optional(),
  isActive: z.boolean().optional()
});

const descriptionOptions = [
  { value: 'sm', label: 'Pequeno', font: 16, size: 180 },
  { value: 'md', label: 'Médio', font: 24, size: 240 },
  { value: 'lg', label: 'Grande', font: 32, size: 300 }
];

const printPresetOptions = [
  { value: 'counter', label: 'Balcão (maior leitura)', columns: 2, qrSize: 300, titleScale: 1.2, itemsPerPage: 4, pagePaddingMm: 12, gapMm: 6 },
  { value: 'table', label: 'Mesa (equilibrado)', columns: 2, qrSize: 240, titleScale: 1.15, itemsPerPage: 6, pagePaddingMm: 10, gapMm: 5 },
  { value: 'compact', label: 'Compacto (mais etiquetas)', columns: 3, qrSize: 185, titleScale: 1.05, itemsPerPage: 9, pagePaddingMm: 8, gapMm: 4 }
];

const pageSize = 10;
const bulkListPageSize = 12;

const normalizeDesc = (value) => {
  const trimmed = (value || '').trim();
  return trimmed.length ? trimmed : null;
};

const TablesPage = () => {
  const api = useApiClient();
  const config = useConfig();
  const { user } = useAuth();
  const { showToast, showConfirm } = useNotification();

  const [tables, setTables] = useState([]);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [areas, setAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [qrSvg, setQrSvg] = useState(null);
  const [description, setDescription] = useState('');
  const [descriptionSize, setDescriptionSize] = useState('md');
  const [printPreset, setPrintPreset] = useState('table');
  const [printShowArea, setPrintShowArea] = useState(true);
  const [printShowDescription, setPrintShowDescription] = useState(true);
  const [downloadBusy, setDownloadBusy] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [includeInactive, setIncludeInactive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: 'numberLabel', direction: 'asc' });
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('create');
  const [editingId, setEditingId] = useState(null);
  const [viewingTable, setViewingTable] = useState(null);
  const [modalTable, setModalTable] = useState(null);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkTables, setBulkTables] = useState([]);
  const [bulkTablesLoading, setBulkTablesLoading] = useState(false);
  const [bulkDescription, setBulkDescription] = useState('');
  const [bulkDescriptionSize, setBulkDescriptionSize] = useState('md');
  const [bulkPrintPreset, setBulkPrintPreset] = useState('table');
  const [bulkPrintShowArea, setBulkPrintShowArea] = useState(true);
  const [bulkPrintShowDescription, setBulkPrintShowDescription] = useState(true);
  const [bulkAreaFilter, setBulkAreaFilter] = useState('__ALL__');
  const [bulkListPage, setBulkListPage] = useState(1);
  const [bulkSelection, setBulkSelection] = useState([]);
  const [linkCopyBusy, setLinkCopyBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [helpSectionKey, setHelpSectionKey] = useState(TABLES_HELP_SECTIONS[0].key);

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors }
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      numberLabel: '',
      area: '',
      isActive: true
    }
  });

  const canManageTables = user?.role === 'Admin' || user?.role === 'Manager' || user?.role === 'AdminMaster';
  const selectedArea = watch('area');

  useEffect(() => {
    document.title = 'Mesas | Orion WaiterCall';
  }, []);

  const loadTables = useCallback(async () => {
    if (!config.restaurantId) {
      return;
    }
    setLoading(true);
    try {
      const response = await api.get(buildRestaurantPath(config.restaurantId, 'tables'), {
        params: {
          includeInactive,
          page,
          pageSize,
          search: searchTerm.trim() || undefined
        }
      });
      const payload = response.data;
      if (payload?.items) {
        setTables(payload.items);
        setTotalItems(payload.totalItems ?? 0);
        setTotalPages(Math.max(1, payload.totalPages ?? 1));
      } else {
        const items = payload || [];
        setTables(items);
        setTotalItems(items.length);
        setTotalPages(1);
      }
    } catch (error) {
      console.error(error);
      showToast('Não foi possível carregar as mesas.', 'error');
    } finally {
      setLoading(false);
    }
  }, [config.restaurantId, api, showToast, includeInactive, page, searchTerm]);

  const loadAreas = useCallback(async () => {
    if (!config.restaurantId) {
      setAreas([]);
      return;
    }

    try {
      const response = await api.get(buildRestaurantPath(config.restaurantId, 'areas'), {
        params: {
          includeInactive: true,
          page: 1,
          pageSize: 100
        }
      });
      const payload = response.data;
      if (payload?.items) {
        setAreas(payload.items);
      } else {
        setAreas(payload || []);
      }
    } catch (error) {
      console.error(error);
      showToast('Não foi possível carregar as áreas.', 'error');
      setAreas([]);
    }
  }, [config.restaurantId, api, showToast]);

  useEffect(() => {
    if (config.restaurantId) {
      loadTables();
      loadAreas();
    } else {
      setTables([]);
      setAreas([]);
      setQrSvg(null);
      setModalTable(null);
    }
  }, [config.restaurantId, config.companyId, loadTables, loadAreas]);

  const openCreate = () => {
    if (!canManageTables) {
      return;
    }
    setModalMode('create');
    setEditingId(null);
    reset({ numberLabel: '', area: '', isActive: true });
    setModalOpen(true);
  };

  const openEdit = (table) => {
    if (!canManageTables) {
      return;
    }
    setModalMode('edit');
    setEditingId(table.id);
    reset({
      numberLabel: table.numberLabel,
      area: table.area || '',
      isActive: table.isActive
    });
    setModalOpen(true);
  };

  const openQrModal = (table) => {
    setModalTable(table);
    setDescription('');
    setDescriptionSize('md');
    setPrintPreset('table');
    setPrintShowArea(true);
    setPrintShowDescription(true);
    setQrSvg(null);
  };

  const openViewModal = (table) => {
    setViewingTable(table);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSaving(false);
  };

  const onSubmit = async (values) => {
    if (!config.restaurantId) {
      showToast('Configure o restaurante para salvar.', 'warning');
      return;
    }
    const editingTable = modalMode === 'edit' ? tables.find((item) => item.id === editingId) : null;
    const willInactivate = modalMode === 'edit' && editingTable?.isActive && values.isActive === false;
    if (willInactivate) {
      const confirmed = await showConfirm({
        title: 'Inativar mesa',
        message: `Deseja inativar a mesa ${editingTable.numberLabel}?`,
        confirmText: 'Inativar',
        cancelText: 'Cancelar',
        type: 'warning'
      });

      if (!confirmed) {
        return;
      }
    }

    setSaving(true);
    try {
      if (modalMode === 'edit' && editingId) {
        await api.put(buildRestaurantPath(config.restaurantId, `tables/${editingId}`), {
          numberLabel: values.numberLabel,
          area: values.area || null,
          isActive: values.isActive ?? true
        });
        showToast('Mesa atualizada com sucesso.', 'success');
      } else {
        await api.post(buildRestaurantPath(config.restaurantId, 'tables'), {
          numberLabel: values.numberLabel,
          area: values.area || null
        });
        showToast('Mesa cadastrada com sucesso.', 'success');
      }
      closeModal();
      loadTables();
    } catch (error) {
      console.error(error);
      const message = error?.response?.data?.message || 'Não foi possível salvar a mesa.';
      showToast(message, 'error');
      setSaving(false);
    }
  };

  const deleteTable = async (table) => {
    if (!config.restaurantId) {
      return;
    }

    const confirmed = await showConfirm({
      title: 'Excluir mesa',
      message: `Deseja excluir a mesa ${table.numberLabel}?`,
      confirmText: 'Excluir',
      cancelText: 'Cancelar'
    });

    if (!confirmed) {
      return;
    }

    setSaving(true);
    try {
      await api.delete(buildRestaurantPath(config.restaurantId, `tables/${table.id}`));
      showToast('Mesa excluída com sucesso.', 'success');
      if (modalTable?.id === table.id) {
        setModalTable(null);
        setQrSvg(null);
      }
      loadTables();
    } catch (error) {
      console.error(error);
      showToast('Não foi possível excluir a mesa.', 'error');
    } finally {
      setSaving(false);
    }
  };

  const descriptionParam = useMemo(() => normalizeDesc(description), [description]);
  const bulkDescriptionParam = useMemo(() => normalizeDesc(bulkDescription), [bulkDescription]);
  const selectedPrintPreset = useMemo(
    () => printPresetOptions.find((option) => option.value === printPreset) || printPresetOptions[1],
    [printPreset]
  );
  const selectedBulkPrintPreset = useMemo(
    () => printPresetOptions.find((option) => option.value === bulkPrintPreset) || printPresetOptions[1],
    [bulkPrintPreset]
  );
  const bulkAreaFilterOptions = useMemo(() => {
    const names = new Set();
    for (const table of bulkTables) {
      const areaName = String(table.area || '').trim();
      if (areaName) {
        names.add(areaName);
      }
    }

    return Array.from(names).sort((a, b) => a.localeCompare(b, 'pt-BR'));
  }, [bulkTables]);
  const bulkFilteredTables = useMemo(() => {
    if (bulkAreaFilter === '__ALL__') {
      return bulkTables;
    }

    if (bulkAreaFilter === '__NONE__') {
      return bulkTables.filter((table) => !String(table.area || '').trim());
    }

    return bulkTables.filter((table) => String(table.area || '').trim() === bulkAreaFilter);
  }, [bulkAreaFilter, bulkTables]);
  const bulkListTotalPages = useMemo(
    () => Math.max(1, Math.ceil(bulkFilteredTables.length / bulkListPageSize)),
    [bulkFilteredTables.length]
  );
  const bulkPaginatedTables = useMemo(() => {
    const startIndex = (bulkListPage - 1) * bulkListPageSize;
    return bulkFilteredTables.slice(startIndex, startIndex + bulkListPageSize);
  }, [bulkFilteredTables, bulkListPage]);
  const estimatedBulkPages = useMemo(() => {
    const perPage = selectedBulkPrintPreset.itemsPerPage || 1;
    return Math.max(1, Math.ceil((bulkSelection.length || 0) / perPage));
  }, [bulkSelection.length, selectedBulkPrintPreset]);
  const qrPublicLink = useMemo(() => {
    if (!modalTable?.id) {
      return '';
    }

    const rawBase = api?.defaults?.baseURL || (typeof window !== 'undefined' ? window.location.origin : '');
    const normalizedBase = String(rawBase || '').trim().replace(/\/+$/, '');

    if (!normalizedBase) {
      return `/q/${modalTable.id}`;
    }

    return `${normalizedBase}/q/${modalTable.id}`;
  }, [api, modalTable]);

  const handleSort = useCallback((key) => {
    setSortConfig((current) => ({
      key,
      direction: current.key === key && current.direction === 'asc' ? 'desc' : 'asc'
    }));
  }, []);

  const visibleTables = useMemo(() => {
    const direction = sortConfig.direction === 'asc' ? 1 : -1;
    const toText = (value) => String(value || '').trim().toLocaleLowerCase('pt-BR');

    return [...tables].sort((a, b) => {
      if (sortConfig.key === 'isActive') {
        if (a.isActive !== b.isActive) {
          return (a.isActive ? -1 : 1) * direction;
        }
        return toText(a.numberLabel).localeCompare(toText(b.numberLabel), 'pt-BR', { numeric: true });
      }

      if (sortConfig.key === 'area') {
        const left = toText(a.area || 'Sem área');
        const right = toText(b.area || 'Sem área');
        const comparison = left.localeCompare(right, 'pt-BR') * direction;
        if (comparison !== 0) {
          return comparison;
        }
        return toText(a.numberLabel).localeCompare(toText(b.numberLabel), 'pt-BR', { numeric: true });
      }

      const comparison = toText(a.numberLabel).localeCompare(toText(b.numberLabel), 'pt-BR', { numeric: true }) * direction;
      if (comparison !== 0) {
        return comparison;
      }

      return String(a.id || '').localeCompare(String(b.id || ''));
    });
  }, [sortConfig, tables]);

  const allSelected = useMemo(
    () => bulkFilteredTables.length > 0 && bulkFilteredTables.every((table) => bulkSelection.includes(table.id)),
    [bulkFilteredTables, bulkSelection]
  );

  useEffect(() => {
    setBulkSelection((current) => current.filter((id) => bulkTables.some((t) => t.id === id)));
  }, [bulkTables]);

  useEffect(() => {
    if (bulkListPage > bulkListTotalPages) {
      setBulkListPage(bulkListTotalPages);
    }
  }, [bulkListPage, bulkListTotalPages]);

  const loadBulkTables = useCallback(async () => {
    if (!config.restaurantId || !bulkModalOpen) {
      return;
    }

    setBulkTablesLoading(true);
    try {
      const aggregated = [];
      let currentPage = 1;
      let discoveredTotalPages = 1;

      while (currentPage <= discoveredTotalPages) {
        const response = await api.get(buildRestaurantPath(config.restaurantId, 'tables'), {
          params: {
            includeInactive: true,
            page: currentPage,
            pageSize: 200
          }
        });

        const payload = response.data;
        if (payload?.items) {
          aggregated.push(...payload.items);
          discoveredTotalPages = Math.max(1, payload.totalPages ?? 1);
        } else {
          aggregated.push(...(payload || []));
          discoveredTotalPages = 1;
        }

        currentPage += 1;
      }

      setBulkTables(aggregated);
    } catch (error) {
      console.error(error);
      showToast('Não foi possível carregar a lista completa de mesas para impressão em massa.', 'error');
      setBulkTables([]);
    } finally {
      setBulkTablesLoading(false);
    }
  }, [api, bulkModalOpen, config.restaurantId, showToast]);

  useEffect(() => {
    loadBulkTables();
  }, [loadBulkTables]);

  const descriptionFont = () => {
    const option = descriptionOptions.find((opt) => opt.value === descriptionSize);
    return option?.font || 16;
  };

  const descriptionImgSize = () => {
    const option = descriptionOptions.find((opt) => opt.value === descriptionSize);
    return option?.size || 180;
  };

  const bulkDescriptionFont = () => {
    const option = descriptionOptions.find((opt) => opt.value === bulkDescriptionSize);
    return option?.font || 16;
  };

  const bulkDescriptionImgSize = () => {
    const option = descriptionOptions.find((opt) => opt.value === bulkDescriptionSize);
    return option?.size || 180;
  };

  const toggleSelectAll = (checked) => {
    if (checked) {
      setBulkSelection((current) => Array.from(new Set([...current, ...bulkFilteredTables.map((table) => table.id)])));
    } else {
      const filteredIds = new Set(bulkFilteredTables.map((table) => table.id));
      setBulkSelection((current) => current.filter((id) => !filteredIds.has(id)));
    }
  };

  const toggleSelectOne = (id, checked) => {
    setBulkSelection((current) => {
      if (checked) {
        return Array.from(new Set([...current, id]));
      }
      return current.filter((item) => item !== id);
    });
  };

  const fallbackCopy = (value) => {
    if (typeof document === 'undefined') {
      throw new Error('Clipboard fallback indisponível.');
    }

    const textArea = document.createElement('textarea');
    textArea.value = value;
    textArea.setAttribute('readonly', '');
    textArea.style.position = 'fixed';
    textArea.style.top = '-1000px';
    textArea.style.left = '-1000px';
    document.body.appendChild(textArea);

    textArea.focus();
    textArea.select();
    const copied = document.execCommand('copy');
    document.body.removeChild(textArea);

    if (!copied) {
      throw new Error('Falha ao copiar o link.');
    }
  };

  const copyQrLink = async () => {
    if (!qrPublicLink) {
      showToast('Link indisponível para esta mesa.', 'warning');
      return;
    }

    try {
      setLinkCopyBusy(true);
      setLinkCopied(false);

      if (navigator?.clipboard?.writeText && window.isSecureContext) {
        await navigator.clipboard.writeText(qrPublicLink);
      } else {
        fallbackCopy(qrPublicLink);
      }

      setLinkCopied(true);
      showToast('Link copiado para a área de transferência.', 'success');
      window.setTimeout(() => setLinkCopied(false), 1800);
    } catch (error) {
      console.error(error);
      try {
        fallbackCopy(qrPublicLink);
        setLinkCopied(true);
        showToast('Link copiado para a área de transferência.', 'success');
        window.setTimeout(() => setLinkCopied(false), 1800);
      } catch (fallbackError) {
        console.error(fallbackError);
        showToast('Não foi possível copiar o link automaticamente. Use Ctrl+C no campo do link.', 'error');
      }
    } finally {
      setLinkCopyBusy(false);
    }
  };

  const generateQr = async () => {
    if (!config.restaurantId || !modalTable) {
      showToast('Selecione uma mesa.', 'warning');
      return;
    }
    try {
      const dataUrl = await fetchQrDataUrl(modalTable.id, descriptionParam, descriptionFont());
      setQrSvg(dataUrl);
      showToast('QR Code gerado com sucesso.', 'success');
    } catch (error) {
      console.error(error);
      showToast('Falha ao gerar QR Code.', 'error');
    }
  };

  const downloadSvg = async () => {
    if (!config.restaurantId || !modalTable) {
      return;
    }
    try {
      setDownloadBusy(true);
      const params = descriptionParam ? { desc: descriptionParam } : {};
      if (descriptionParam) params.fontSize = descriptionFont();
      
      const response = await api.get(buildRestaurantPath(config.restaurantId, `tables/${modalTable.id}/qr.svg`), {
        params,
        responseType: 'text'
      });
      const blob = new Blob([response.data], { type: 'image/svg+xml' });
      saveAs(blob, filename('svg'));
      showToast('SVG baixado com sucesso.', 'success');
    } catch (error) {
      console.error(error);
      showToast('Não foi possível baixar o SVG.', 'error');
    } finally {
      setDownloadBusy(false);
    }
  };

  const downloadPng = async () => {
    if (!config.restaurantId || !modalTable) {
      return;
    }
    try {
      setDownloadBusy(true);
      const response = await api.get(buildRestaurantPath(config.restaurantId, `tables/${modalTable.id}/qr.png`), {
        params: descriptionParam ? { desc: descriptionParam } : {},
        responseType: 'blob'
      });
      saveAs(response.data, filename('png'));
      showToast('PNG baixado com sucesso.', 'success');
    } catch (error) {
      console.error(error);
      showToast('Não foi possível baixar o PNG.', 'error');
    } finally {
      setDownloadBusy(false);
    }
  };

  const downloadAll = async () => {
    if (!config.restaurantId || !bulkTables.length) {
      showToast('Cadastre mesas antes de baixar.', 'warning');
      return;
    }
    const selectedIds = bulkSelection;
    if (!selectedIds.length) {
      showToast('Selecione ao menos uma mesa.', 'warning');
      return;
    }
    try {
      setBulkBusy(true);
      const zip = new JSZip();
      for (const table of bulkTables.filter((t) => selectedIds.includes(t.id))) {
        const params = bulkDescriptionParam ? { desc: bulkDescriptionParam } : {};
        if (bulkDescriptionParam) params.fontSize = bulkDescriptionFont();

        const svgResponse = await api.get(buildRestaurantPath(config.restaurantId, `tables/${table.id}/qr.svg`), {
          params,
          responseType: 'text'
        });
        zip.file(`mesa-${table.numberLabel}.svg`, svgResponse.data);
        const pngResponse = await api.get(buildRestaurantPath(config.restaurantId, `tables/${table.id}/qr.png`), {
          params,
          responseType: 'blob'
        });
        zip.file(`mesa-${table.numberLabel}.png`, pngResponse.data);
      }
      const content = await zip.generateAsync({ type: 'blob' });
      saveAs(content, `waitercall-qr-${config.restaurantId}.zip`);
      showToast('ZIP gerado com sucesso.', 'success');
      setBulkModalOpen(false);
    } catch (error) {
      console.error(error);
      showToast('Falha ao gerar o ZIP.', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const fetchQrDataUrl = async (tableId, desc, fontSize = 16) => {
    const params = desc ? { desc, fontSize } : {};
    const response = await api.get(buildRestaurantPath(config.restaurantId, `tables/${tableId}/qr.svg`), {
      params,
      responseType: 'text'
    });
    const svgText = response.data;
    return `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(svgText)))}`;
  };

  const printCurrent = async () => {
    if (!modalTable) {
      showToast('Selecione uma mesa.', 'warning');
      return;
    }

    let dataUrlToPrint;
    try {
      // Print QR without embedded description to avoid duplicated text in print layout.
      dataUrlToPrint = await fetchQrDataUrl(modalTable.id, null, descriptionFont());
    } catch (error) {
      console.error(error);
      showToast('Falha ao gerar QR Code para impressão.', 'error');
      return;
    }

    openPrintWindow([
      {
        title: `${modalTable.numberLabel}`,
        area: printShowArea ? (modalTable.area || '') : '',
        description: printShowDescription ? descriptionParam : null,
        fontSize: descriptionFont(),
        imgSize: selectedPrintPreset.qrSize,
        dataUrl: dataUrlToPrint
      }
    ], selectedPrintPreset);
  };


  const printBulk = async () => {
    if (!config.restaurantId || !bulkTables.length) {
      showToast('Cadastre mesas antes de imprimir.', 'warning');
      return;
    }

    const selectedIds = bulkSelection;
    if (!selectedIds.length) {
      showToast('Selecione ao menos uma mesa.', 'warning');
      return;
    }

    try {
      setBulkBusy(true);
      const items = [];
      const tablesToPrint = bulkTables.filter((t) => selectedIds.includes(t.id));

      for (const table of tablesToPrint) {
        const params = {};
        const response = await api.get(buildRestaurantPath(config.restaurantId, `tables/${table.id}/qr.svg`), {
          params,
          responseType: 'text'
        });
        const dataUrl = `data:image/svg+xml;base64,${btoa(unescape(encodeURIComponent(response.data)))}`;
        items.push({
          title: `${table.numberLabel}`,
          area: bulkPrintShowArea ? (table.area || '') : '',
          description: bulkPrintShowDescription ? bulkDescriptionParam : null,
          fontSize: bulkDescriptionFont(),
          imgSize: selectedBulkPrintPreset.qrSize,
          dataUrl
        });
      }
      openPrintWindow(items, selectedBulkPrintPreset);
      showToast('Preparando impressão...', 'info');
    } catch (error) {
      console.error(error);
      showToast('Não foi possível gerar as impressões.', 'error');
    } finally {
      setBulkBusy(false);
    }
  };

  const filename = (extension) => {
    const raw = modalTable?.numberLabel || 'mesa';
    const slug = raw
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-zA-Z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '')
      .toLowerCase();
    return `waitercall-${config.restaurantId || 'restaurante'}-${slug || 'mesa'}.${extension}`;
  };

  const escapeHtml = (value) => String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

  const openPrintWindow = (items, preset = printPresetOptions[1]) => {
    if (!items.length || typeof window === 'undefined') {
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.style.position = 'absolute';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = '0';
    document.body.appendChild(iframe);

    const styles = `
      @page { size: A4 portrait; margin: ${preset.pagePaddingMm}mm; }
      * { box-sizing: border-box; margin: 0; padding: 0; }
      body { font-family: 'Inter', Arial, sans-serif; color: #000; }
      .grid { display: grid; gap: ${preset.gapMm}mm; grid-template-columns: repeat(${preset.columns}, 1fr); }
      .card { display: flex; flex-direction: column; align-items: center; gap: 8px; padding: 10px; border: 1px dashed #a1a1aa; border-radius: 8px; page-break-inside: avoid; break-inside: avoid; }
      .card header { text-align: center; }
      .card h1 { font-weight: 800; margin-bottom: 3px; line-height: 1.15; letter-spacing: 0.01em; }
      .card .area { color: #27272a; line-height: 1.2; }
      .card .desc { color: #111827; text-align: center; line-height: 1.3; max-width: 95%; }
      .card img { object-fit: contain; }
      @media print {
        .card { break-inside: avoid; }
      }
    `;

    const body = items
      .map(
        (item) => `
        <section class="card">
          <header>
            <h1 style="font-size:${Math.round(item.fontSize * preset.titleScale)}px;">${escapeHtml(item.title)}</h1>
            ${item.area ? `<div class="area" style="font-size:${Math.max(12, Math.round(item.fontSize * 0.75))}px;">${escapeHtml(item.area)}</div>` : ''}
          </header>
          <img src="${item.dataUrl}" alt="${escapeHtml(item.title)}" style="width:${item.imgSize}px;height:${item.imgSize}px;">
          ${item.description ? `<p class="desc" style="font-size:${Math.max(11, Math.round(item.fontSize * 0.62))}px;">${escapeHtml(item.description)}</p>` : ''}
        </section>
      `
      )
      .join('');

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <meta charset="utf-8">
          <title>QR Codes - WaiterCall</title>
          <style>${styles}</style>
        </head>
        <body>
          <div class="grid">${body}</div>
        </body>
      </html>
    `;

    const doc = iframe.contentWindow.document;
    doc.open();
    doc.write(html);
    doc.close();

    const doPrint = () => {
      try {
        iframe.contentWindow.focus();
        iframe.contentWindow.print();
      } catch (e) {
        console.error('Print failed', e);
      } finally {
        setTimeout(() => {
          if (document.body.contains(iframe)) {
            document.body.removeChild(iframe);
          }
        }, 2000);
      }
    };

    if (iframe.contentWindow.document.readyState === 'complete') {
      setTimeout(doPrint, 500);
    } else {
      iframe.onload = () => setTimeout(doPrint, 500);
    }
  };

  const availableAreaOptions = useMemo(() => {
    const normalizedCurrent = (selectedArea || '').trim();
    const activeAreas = areas.filter((area) => area.isActive);

    if (!normalizedCurrent) {
      return activeAreas;
    }

    const alreadyExists = activeAreas.some((area) => area.name === normalizedCurrent);
    if (alreadyExists) {
      return activeAreas;
    }

    return [...activeAreas, { id: `legacy-${normalizedCurrent}`, name: normalizedCurrent, isActive: false }];
  }, [areas, selectedArea]);

  useEffect(() => {
    if (page > totalPages) {
      setPage(totalPages);
    }
  }, [page, totalPages]);

  useEffect(() => {
    setLinkCopied(false);
    setLinkCopyBusy(false);
  }, [modalTable]);

  return (
    <section>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-xl font-semibold text-gray-900">Mesas</h1>
          <p className="mt-2 text-sm text-gray-700">Visualize e gere QR Codes para cada mesa.</p>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2 sm:ml-16 sm:mt-0 sm:flex-none">
          {canManageTables ? (
            <button
              type="button"
              onClick={openCreate}
              className="inline-flex flex-1 items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 sm:flex-none"
            >
              Nova mesa
            </button>
          ) : null}
          <button
            type="button"
            onClick={() => {
              setBulkModalOpen(true);
              setBulkTables([]);
              setBulkSelection([]);
              setBulkDescription('');
              setBulkDescriptionSize('md');
              setBulkPrintPreset('table');
              setBulkPrintShowArea(true);
              setBulkPrintShowDescription(true);
              setBulkAreaFilter('__ALL__');
              setBulkListPage(1);
            }}
            className="inline-flex flex-1 items-center justify-center rounded-md border border-indigo-200 bg-white px-3 py-2 text-sm font-semibold text-indigo-700 shadow-sm hover:bg-indigo-50 disabled:opacity-60 sm:flex-none"
            disabled={!tables.length}
          >
            QR em massa
          </button>
          <ContextHelpButton
            label="Ajuda sobre mesas"
            onClick={() => {
              setHelpSectionKey('registry');
              setIsHelpOpen(true);
            }}
          />
        </div>
      </div>

      <div className="mt-4 flex flex-col sm:flex-row items-center gap-4">
        <input
          type="text"
          placeholder="Buscar por identificação ou área..."
          value={searchTerm}
          onChange={(e) => {
            setSearchTerm(e.target.value);
            setPage(1);
          }}
          className="block w-full rounded-md border-0 py-1.5 pl-3 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6 max-w-md"
        />
        <div className="w-full sm:w-auto">
          <div className="flex h-[38px] items-center gap-3">
            <Switch
              checked={!includeInactive}
              onChange={(checked) => {
                setIncludeInactive(!checked);
                setPage(1);
              }}
              className={`${!includeInactive ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
            >
              <span className="sr-only">Apenas ativas</span>
              <span
                aria-hidden="true"
                className={`${!includeInactive ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
            <span className="text-sm text-gray-600">Apenas ativas</span>
          </div>
        </div>
      </div>

      <div className="mt-6 sm:hidden space-y-3">
        {loading ? (
          <div className="rounded-lg border border-gray-200 bg-white p-4 text-sm text-gray-600">Carregando mesas...</div>
        ) : visibleTables.length === 0 ? (
          <div className="rounded-lg border border-dashed border-gray-300 bg-gray-50 p-4 text-sm text-gray-600">Nenhuma mesa encontrada.</div>
        ) : (
          visibleTables.map((table) => (
            <article key={table.id} onClick={() => openViewModal(table)} className="cursor-pointer rounded-lg border border-gray-200 bg-white p-4 shadow-sm hover:bg-gray-50">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{table.numberLabel}</h3>
                  <p className="text-xs text-gray-600">{table.area || 'Sem área'}</p>
                </div>
                <StatusPill label={table.isActive ? 'Ativa' : 'Inativa'} variant={table.isActive ? 'success' : 'muted'} />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button type="button" onClick={(e) => { e.stopPropagation(); openQrModal(table); }} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">QR Code</button>
                {canManageTables ? (
                  <button type="button" onClick={(e) => { e.stopPropagation(); openEdit(table); }} className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-semibold text-gray-700 hover:bg-gray-50">Editar</button>
                ) : null}
                {canManageTables ? (
                  <button type="button" onClick={(e) => { e.stopPropagation(); deleteTable(table); }} disabled={saving} className="inline-flex items-center gap-1 rounded-md border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700 hover:bg-red-50 disabled:opacity-60"><TrashIcon className="h-4 w-4" />Excluir</button>
                ) : null}
              </div>
            </article>
          ))
        )}
      </div>

      <div className="mt-8 hidden sm:block">
        <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              {loading ? (
                <div className="bg-white p-4 text-sm text-gray-600">Carregando mesas...</div>
              ) : (
                <table className="min-w-full divide-y divide-gray-300 bg-white">
                  <thead className="bg-gray-50">
                    <tr>
                      <SortableTh
                        label="Mesa"
                        sortKey="numberLabel"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
                      />
                      <SortableTh
                        label="Área"
                        sortKey="area"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      />
                      <SortableTh
                        label="Status"
                        sortKey="isActive"
                        sortConfig={sortConfig}
                        onSort={handleSort}
                        className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
                      />
                      <th className="relative py-3.5 pl-3 pr-4 sm:pr-6"><span className="sr-only">Ações</span></th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200 bg-white">
                    {visibleTables.length === 0 ? (
                      <tr>
                        <td colSpan={4} className="py-8 text-center text-sm text-gray-500">Nenhuma mesa encontrada com os filtros atuais.</td>
                      </tr>
                    ) : (
                      visibleTables.map((table) => (
                        <tr key={table.id} onClick={() => openViewModal(table)} className="cursor-pointer hover:bg-gray-50">
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">{table.numberLabel}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">{table.area || 'Sem área'}</td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                            <StatusPill label={table.isActive ? 'Ativa' : 'Inativa'} variant={table.isActive ? 'success' : 'muted'} />
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                            <button onClick={(e) => { e.stopPropagation(); openQrModal(table); }} title="QR Code" className="mr-4 text-indigo-600 hover:text-indigo-900">
                              <QrCodeIcon className="h-5 w-5" />
                            </button>
                            {canManageTables ? (
                              <button onClick={(e) => { e.stopPropagation(); openEdit(table); }} title="Editar" className="mr-4 text-indigo-600 hover:text-indigo-900">
                                <PencilIcon className="h-5 w-5" />
                              </button>
                            ) : null}
                            {canManageTables ? (
                              <button onClick={(e) => { e.stopPropagation(); deleteTable(table); }} title="Excluir" className="text-red-600 hover:text-red-900">
                                <TrashIcon className="h-5 w-5" />
                              </button>
                            ) : null}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              )}

              {!loading && totalItems > 0 ? (
                <Pagination
                  currentPage={page}
                  totalPages={totalPages}
                  onPageChange={setPage}
                  totalItems={totalItems}
                  pageSize={pageSize}
                />
              ) : null}
            </div>
          </div>
        </div>
      </div>

      {modalOpen ? (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto my-4 max-h-[92dvh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-center justify-between gap-2">
              <h3 className="text-lg font-semibold text-slate-900">{modalMode === 'edit' ? 'Editar mesa' : 'Cadastrar mesa'}</h3>
              <button type="button" onClick={closeModal} className="text-sm font-semibold text-slate-500 hover:text-slate-800">
                Fechar
              </button>
            </div>
            <form className="mt-4 space-y-3" onSubmit={handleSubmit(onSubmit)}>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-800">Identificação *</label>
                <input
                  type="text"
                  {...register('numberLabel')}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
                {errors.numberLabel ? <p className="text-sm text-red-600">{errors.numberLabel.message}</p> : null}
              </div>
              <div className="space-y-1">
                <label className="text-sm font-semibold text-slate-800">Área</label>
                <select
                  {...register('area')}
                  className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                >
                  <option value="">Sem área</option>
                  {availableAreaOptions.map((area) => (
                    <option key={area.id} value={area.name}>
                      {area.name}
                      {area.isActive ? '' : ' (inativa)'}
                    </option>
                  ))}
                </select>
                {!availableAreaOptions.length ? (
                  <p className="text-xs text-slate-500">Cadastre áreas para selecionar uma opção.</p>
                ) : null}
              </div>
              {modalMode === 'edit' ? (
                <label className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <input
                    type="checkbox"
                    {...register('isActive')}
                    className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  Mesa ativa
                </label>
              ) : null}
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={closeModal}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-70"
                >
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {viewingTable ? (
        <div className="fixed inset-0 z-40 overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto my-4 max-h-[92dvh] w-full max-w-xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-2 border-b border-gray-100 pb-3">
              <div>
                <h3 className="text-lg font-semibold text-gray-900">Detalhes da mesa</h3>
                <p className="text-sm text-gray-500">Informações de cadastro e status</p>
              </div>
              <button type="button" onClick={() => setViewingTable(null)} className="text-sm font-semibold text-slate-500 hover:text-slate-800">
                Fechar
              </button>
            </div>

            <div className="mt-4 space-y-3 text-sm text-gray-700">
              <div>
                <p className="text-xs font-medium uppercase text-gray-500">Identificação</p>
                <p className="text-base font-medium text-gray-900">{viewingTable.numberLabel}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-gray-500">Área</p>
                <p>{viewingTable.area || 'Sem área'}</p>
              </div>
              <div>
                <p className="text-xs font-medium uppercase text-gray-500">Status</p>
                <StatusPill label={viewingTable.isActive ? 'Ativa' : 'Inativa'} variant={viewingTable.isActive ? 'success' : 'muted'} />
              </div>
            </div>

            <div className="mt-6 flex justify-end border-t border-gray-200 pt-4">
              <button
                type="button"
                onClick={() => setViewingTable(null)}
                className="rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {modalTable ? (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto my-4 max-h-[94dvh] w-full max-w-6xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Gerar QR Code</p>
                <h3 className="text-xl font-semibold text-slate-900">
                  {modalTable.numberLabel} {modalTable.area ? `(${modalTable.area})` : ''}
                </h3>
              </div>
              <button type="button" onClick={() => setModalTable(null)} className="text-sm font-semibold text-slate-500 hover:text-slate-800">
                Fechar
              </button>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-[1.35fr,0.95fr]">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-semibold text-slate-800">Descrição opcional</label>
                    <textarea
                      value={description}
                      onChange={(e) => setDescription(e.target.value)}
                      rows={2}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Escaneie aqui para chamar um garçom"
                    />
                  </div>

                  <div className="space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <label className="text-sm font-semibold text-slate-800">Tamanho do texto</label>
                    <select
                      value={descriptionSize}
                      onChange={(e) => setDescriptionSize(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {descriptionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <label className="text-sm font-semibold text-slate-800">Preset de impressão</label>
                    <select
                      value={printPreset}
                      onChange={(e) => setPrintPreset(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {printPresetOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <span className="text-sm font-semibold text-slate-800">Exibir nome área</span>
                    <Switch
                      checked={printShowArea}
                      onChange={setPrintShowArea}
                      className={`${printShowArea ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
                    >
                      <span className="sr-only">Exibir nome área na impressão</span>
                      <span
                        aria-hidden="true"
                        className={`${printShowArea ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </Switch>
                  </div>

                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <span className="text-sm font-semibold text-slate-800">Exibir descrição</span>
                    <Switch
                      checked={printShowDescription}
                      onChange={setPrintShowDescription}
                      className={`${printShowDescription ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
                    >
                      <span className="sr-only">Exibir descrição na impressão</span>
                      <span
                        aria-hidden="true"
                        className={`${printShowDescription ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </Switch>
                  </div>

                  <div className="space-y-2 md:col-span-2">
                    <label className="text-sm font-semibold text-slate-800">Link da mesa</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        readOnly
                        value={qrPublicLink}
                        onFocus={(e) => e.target.select()}
                        className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        aria-label="Link público da mesa"
                      />
                      <button
                        type="button"
                        onClick={copyQrLink}
                        disabled={linkCopyBusy || !qrPublicLink}
                        title={linkCopied ? 'Link copiado' : 'Copiar link'}
                        aria-label={linkCopied ? 'Link copiado' : 'Copiar link'}
                        className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-70"
                      >
                        {linkCopied ? (
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        ) : (
                          <ClipboardDocumentIcon className="h-5 w-5" aria-hidden="true" />
                        )}
                      </button>
                    </div>
                    <p className="text-xs text-slate-500">Use este link para compartilhar o acesso direto da mesa.</p>
                  </div>

                  <div className="grid gap-2 md:col-span-2 lg:grid-cols-2 2xl:grid-cols-4">
                    <button
                      type="button"
                      onClick={generateQr}
                      className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700"
                    >
                      Gerar QR
                    </button>
                    <button
                      type="button"
                      onClick={downloadSvg}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      disabled={downloadBusy || !qrSvg}
                    >
                      Baixar SVG
                    </button>
                    <button
                      type="button"
                      onClick={downloadPng}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                      disabled={downloadBusy || !qrSvg}
                    >
                      Baixar PNG
                    </button>
                    <button
                      type="button"
                      onClick={printCurrent}
                      className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100"
                    >
                      Imprimir
                    </button>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-4">
                <p className="text-sm font-semibold text-slate-800">Pré-visualização</p>
                <p className="text-xs text-slate-500">Gere o QR para ver a imagem, baixar ou imprimir.</p>
                <div className="mt-3">
                  <QrPreview svgDataUrl={qrSvg} description={descriptionParam} fontSize={descriptionFont()} />
                </div>
              </div>
            </div>

          </div>
        </div>
      ) : null}

      {bulkModalOpen ? (
        <div className="fixed inset-0 z-[80] overflow-y-auto bg-black/40 p-4">
          <div className="mx-auto my-4 max-h-[94dvh] w-full max-w-7xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl">
            <div className="flex items-start justify-between gap-2">
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">QR Code em massa</p>
                <h3 className="text-xl font-semibold text-slate-900">Selecione as mesas para gerar o ZIP</h3>
              </div>
              <button
                type="button"
                onClick={() => setBulkModalOpen(false)}
                className="text-sm font-semibold text-slate-500 hover:text-slate-800"
              >
                Fechar
              </button>
            </div>

            <div className="mt-4 grid gap-4 xl:grid-cols-[1.3fr,0.85fr]">
              <div className="space-y-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
                <div className="grid gap-3 md:grid-cols-2">
                  <label className="space-y-2 text-sm font-semibold text-slate-800 md:col-span-2">
                    <span>Descrição opcional</span>
                    <textarea
                      value={bulkDescription}
                      onChange={(e) => setBulkDescription(e.target.value)}
                      rows={2}
                      className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-medium text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                      placeholder="Escaneie aqui para chamar um garçom"
                    />
                  </label>
                  <div className="space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <label className="text-sm font-semibold text-slate-800">Tamanho do texto</label>
                    <select
                      value={bulkDescriptionSize}
                      onChange={(e) => setBulkDescriptionSize(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {descriptionOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <label className="text-sm font-semibold text-slate-800">Preset de impressão</label>
                    <select
                      value={bulkPrintPreset}
                      onChange={(e) => setBulkPrintPreset(e.target.value)}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      {printPresetOptions.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <span className="text-sm font-semibold text-slate-800">Exibir nome área</span>
                    <Switch
                      checked={bulkPrintShowArea}
                      onChange={setBulkPrintShowArea}
                      className={`${bulkPrintShowArea ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
                    >
                      <span className="sr-only">Exibir nome área na impressão em massa</span>
                      <span
                        aria-hidden="true"
                        className={`${bulkPrintShowArea ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </Switch>
                  </div>
                  <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-2">
                    <span className="text-sm font-semibold text-slate-800">Exibir descrição</span>
                    <Switch
                      checked={bulkPrintShowDescription}
                      onChange={setBulkPrintShowDescription}
                      className={`${bulkPrintShowDescription ? 'bg-indigo-600' : 'bg-gray-200'} relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-0`}
                    >
                      <span className="sr-only">Exibir descrição na impressão em massa</span>
                      <span
                        aria-hidden="true"
                        className={`${bulkPrintShowDescription ? 'translate-x-5' : 'translate-x-0'} pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out`}
                      />
                    </Switch>
                  </div>
                  <div className="space-y-1 rounded-lg border border-slate-200 bg-white px-3 py-2 md:col-span-2 lg:col-span-1">
                    <label className="text-sm font-semibold text-slate-800">Filtrar por área</label>
                    <select
                      value={bulkAreaFilter}
                      onChange={(e) => {
                        setBulkAreaFilter(e.target.value);
                        setBulkListPage(1);
                      }}
                      className="w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-900 shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="__ALL__">Todas as áreas</option>
                      <option value="__NONE__">Sem área</option>
                      {bulkAreaFilterOptions.map((areaName) => (
                        <option key={areaName} value={areaName}>
                          {areaName}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 md:col-span-2 lg:col-span-1">
                    <input
                      id="selectAll"
                      type="checkbox"
                      checked={allSelected}
                      onChange={(e) => toggleSelectAll(e.target.checked)}
                      className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                    />
                    <label htmlFor="selectAll" className="text-sm font-semibold text-slate-800">
                      Selecionar todas do filtro ({bulkFilteredTables.length})
                    </label>
                  </div>
                </div>

                <div className="max-h-[44dvh] space-y-2 overflow-auto rounded-lg border border-slate-200 bg-white p-3">
                  {bulkTablesLoading ? (
                    <p className="text-center text-sm font-semibold text-slate-600">Carregando mesas para impressão em massa...</p>
                  ) : bulkPaginatedTables.map((table) => (
                    <label
                      key={table.id}
                      className="flex items-center justify-between gap-3 rounded-lg border border-slate-100 px-3 py-2 text-sm shadow-sm"
                    >
                      <div className="space-y-0.5">
                        <p className="font-semibold text-slate-900">{table.numberLabel}</p>
                        <p className="text-xs text-slate-600">{table.area || 'Sem área'}</p>
                      </div>
                      <input
                        type="checkbox"
                        checked={bulkSelection.includes(table.id)}
                        onChange={(e) => toggleSelectOne(table.id, e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
                      />
                    </label>
                  ))}
                  {!bulkTablesLoading && !bulkPaginatedTables.length ? (
                    <p className="text-center text-sm font-semibold text-slate-600">Nenhuma mesa cadastrada.</p>
                  ) : null}
                </div>
                {!bulkTablesLoading && bulkFilteredTables.length > 0 ? (
                  <Pagination
                    currentPage={bulkListPage}
                    totalPages={bulkListTotalPages}
                    onPageChange={setBulkListPage}
                    totalItems={bulkFilteredTables.length}
                    pageSize={bulkListPageSize}
                  />
                ) : null}
              </div>

              <div className="space-y-3 rounded-xl border border-slate-200 bg-white p-4 shadow-sm xl:sticky xl:top-4">
                <p className="text-sm font-semibold text-slate-800">Resumo</p>
                <p className="text-xs text-slate-500">Selecione mesas e clique para baixar o ZIP com os QR Codes.</p>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
                  <p>
                    Selecionadas: <span className="font-semibold">{bulkSelection.length}</span>
                  </p>
                  <p className="mt-1">
                    Estimativa de páginas: <span className="font-semibold">{estimatedBulkPages}</span>
                  </p>
                  <p className="mt-1">
                    Descrição: <span className="font-semibold">{bulkDescriptionParam || 'Nenhuma'}</span>
                  </p>
                </div>
                <button
                  type="button"
                  onClick={downloadAll}
                  disabled={bulkBusy || !bulkTables.length || !bulkSelection.length}
                  className="w-full rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-indigo-700 disabled:opacity-70"
                >
                  {bulkBusy ? 'Gerando ZIP...' : 'Baixar ZIP'}
                </button>
                <button
                  type="button"
                  onClick={printBulk}
                  disabled={bulkBusy || !bulkTables.length || !bulkSelection.length}
                  className="w-full rounded-lg border border-slate-300 px-4 py-2 text-sm font-semibold text-slate-700 hover:bg-slate-100 disabled:opacity-70"
                >
                  Imprimir
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <ContextHelpDrawer
        open={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title="Mesas"
        description="Revise cadastro, QR Codes e impressão antes de preparar a operação do salão."
        sections={TABLES_HELP_SECTIONS}
        activeSectionKey={helpSectionKey}
        onSectionChange={setHelpSectionKey}
      />
    </section>
  );
};

export default TablesPage;
